import { Radio as Base } from '@base-ui/react/radio'
import { RadioGroup as BaseGroup } from '@base-ui/react/radio-group'
import type { ReactNode } from 'react'

import { cn } from '../../lib/cn'
import s from './Radio.module.css'

/**
 * Bouton radio — report de `.opt .radio` du prototype validé (`mocks/ghp-bo/app.css`), où il
 * marque l'option retenue dans une **colonne de rangées** : l'équipe d'un point interne, l'agent
 * d'un rendez-vous.
 *
 * **Primitive `@base-ui/react`**, et pas un `<input type="radio">` stylé, pour la même raison que
 * `Checkbox` : le marqueur du prototype est une pastille dessinée dans un disque, ce qu'un `input`
 * natif ne laisse pas remplacer sans `appearance: none` puis une reconstruction complète. La
 * primitive apporte en plus ce qu'un lot d'`input` de même `name` ne donne pas seul — le
 * déplacement aux flèches dans le groupe, et un seul arrêt de tabulation pour l'ensemble.
 *
 * ⚠️ Les trois noms d'attributs lus en CSS (`data-checked`, `data-unchecked`, `data-disabled`)
 * viennent de `RadioRootDataAttributes` du paquet, pas d'une supposition — `packages/ui/CLAUDE.md`
 * prévient qu'ils varient d'une primitive à l'autre.
 *
 * ⚠️ **Le disque n'est pas la cible du clic.** Chez le prototype, c'est la rangée entière (`.opt`)
 * qui se clique, et sa mise en forme appartient à l'écran — comme `new-team.module.css` le fait
 * déjà pour la rangée à case. Ce composant ne dessine que le marqueur ; il n'impose ni bordure de
 * rangée, ni fond retenu, ni hauteur.
 */
export interface RadioProps<Value> {
  /** La valeur que cette option porte dans son groupe. */
  value: Value
  /** Rattachement à un `<label>` externe, quand la rangée porte le libellé. */
  id?: string
  /** Rattachement à un libellé déjà rendu ailleurs — le nom de l'équipe, par exemple. */
  'aria-labelledby'?: string
  disabled?: boolean
  className?: string
}

export function Radio<Value>({
  value,
  id,
  'aria-labelledby': labelledBy,
  disabled,
  className,
}: RadioProps<Value>) {
  return (
    <Base.Root
      id={id}
      aria-labelledby={labelledBy}
      value={value}
      disabled={disabled}
      className={cn(s.root, className)}
    >
      {/* L'indicateur n'est monté que retenu : c'est la primitive qui le décide, et le CSS n'a
          donc pas à masquer une pastille toujours présente. */}
      <Base.Indicator className={s.indicator} />
    </Base.Root>
  )
}

/**
 * Le groupe qui tient la sélection — le `.stack` du prototype, et le seul endroit où la valeur
 * retenue existe.
 *
 * **Contrôlé seulement** (`value` + `onValueChange`), comme `Checkbox` et `Modal` : la sélection
 * appartient à l'état de l'écran, un état interne en ferait une seconde source de vérité.
 *
 * ⚠️ **`value` accepte `null`**, qui est l'état « rien de retenu » à l'ouverture d'une modale.
 * `undefined` ferait basculer la primitive en mode non contrôlé — et le groupe cesserait
 * silencieusement d'obéir à l'écran.
 *
 * ⚠️ **Un groupe de radios se nomme, sans quoi il n'est qu'une pile de boutons.** `aria-labelledby`
 * pointe le libellé visible du champ ; `Field` rend l'identifiant qu'il faut y passer (`labelId`).
 */
export interface RadioGroupProps<Value> {
  value: Value | null
  onValueChange: (value: Value) => void
  /** L'identifiant du libellé visible du groupe — `labelId` de `Field`. */
  'aria-labelledby'?: string
  /** Le nom accessible **quand aucun libellé n'est rendu à côté**, et à ce titre seulement. */
  'aria-label'?: string
  disabled?: boolean
  children?: ReactNode
  className?: string
}

export function RadioGroup<Value>({
  value,
  onValueChange,
  'aria-labelledby': labelledBy,
  'aria-label': label,
  disabled,
  children,
  className,
}: RadioGroupProps<Value>) {
  return (
    // ⚠️ **La primitive est instanciée sur `Value | null`, et pas sur `Value`.** Son type déclare
    // `value?: Value | undefined` : passer `undefined` pour dire « rien de retenu » la ferait
    // basculer en mode **non contrôlé**, et le groupe cesserait d'obéir à l'écran sans que rien
    // n'échoue. `null` est distinct d'`undefined` à l'exécution — elle le voit donc contrôlé, et
    // c'est le type qu'il faut élargir, pas le comportement qu'il faut contourner.
    <BaseGroup<Value | null>
      value={value}
      // La primitive passe aussi un second argument (détails de l'événement) : on ne le propage
      // pas, pour ne pas faire fuiter son API dans celle du design system.
      onValueChange={(next) => {
        // `null` n'arrive pas d'un clic — aucune option ne porte cette valeur. Le repli existe
        // parce que le type l'exige, et il ne masque rien.
        if (next !== null) onValueChange(next)
      }}
      aria-labelledby={labelledBy}
      aria-label={label}
      disabled={disabled}
      className={cn(s.group, className)}
    >
      {children}
    </BaseGroup>
  )
}
